"use client";
import { useCallback, useEffect, useState } from "react";
import { PixelText } from "@/components/ui/pixel-text";

export type Action = "fight" | "bag" | "surggie" | "run";

interface BattleOverlayHDProps {
  enabled: boolean;
  onSelect: (action: Action) => void;
  prompt?: string;
  /** When false, the Run button is grayed out (trainer battle). Default: true */
  canRun?: boolean;
}

// 2x2 grid layout, row-major:
//   index 0 (top-left)  = fight   index 1 (top-right)    = bag
//   index 2 (bot-left)  = surggie index 3 (bot-right)    = run
const ACTIONS: { action: Action; label: string }[] = [
  { action: "fight", label: "Fight" },
  { action: "bag", label: "Bag" },
  { action: "surggie", label: "Surggie" },
  { action: "run", label: "Run" },
];

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";

export function BattleOverlayHD({
  enabled,
  onSelect,
  prompt = "What will you do?",
  canRun = true,
}: BattleOverlayHDProps) {
  const [cursor, setCursor] = useState(0);
  const [focused, setFocused] = useState(true);

  const move = useCallback((dx: number, dy: number) => {
    setCursor((c) => {
      const col = c % 2;
      const row = (c - col) / 2;
      const nextCol = Math.max(0, Math.min(1, col + dx));
      const nextRow = Math.max(0, Math.min(1, row + dy));
      return nextRow * 2 + nextCol;
    });
  }, []);

  useEffect(() => {
    if (!enabled || !focused) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      if (
        key === "ArrowUp" ||
        key === "ArrowDown" ||
        key === "ArrowLeft" ||
        key === "ArrowRight" ||
        key === "Enter" ||
        key === "z" ||
        key === "Z" ||
        key === "x" ||
        key === "X" ||
        key === "Escape"
      ) {
        e.preventDefault();
      }

      switch (key) {
        case "ArrowUp":
          move(0, -1);
          break;
        case "ArrowDown":
          move(0, 1);
          break;
        case "ArrowLeft":
          move(-1, 0);
          break;
        case "ArrowRight":
          move(1, 0);
          break;
        case "Enter":
        case "z":
        case "Z": {
          const chosen = ACTIONS[cursor].action;
          if (chosen === "run" && !canRun) break;
          onSelect(chosen);
          break;
        }
        case "x":
        case "X":
        case "Escape":
          setFocused(false);
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, focused, cursor, move, onSelect, canRun]);

  const dimmed = !enabled;

  return (
    <div
      className={`flex flex-col gap-3 ${
        dimmed ? "opacity-50 pointer-events-none" : ""
      }`}
      style={{
        backgroundColor: CREAM,
        border: `2px solid ${INK}`,
        padding: "1rem 1.25rem",
        imageRendering: "pixelated",
      }}
      onMouseEnter={() => setFocused(true)}
    >
      <PixelText
        as="div"
        glow="none"
        size="md"
        className="!text-[#1a1a1a] !tracking-normal !normal-case"
      >
        {prompt}
      </PixelText>

      <div
        className="grid grid-cols-2 gap-2"
        role="menu"
        aria-label="Battle actions"
      >
        {ACTIONS.map((entry, i) => {
          const isRun = entry.action === "run";
          const runDisabled = isRun && !canRun;
          const active = i === cursor && focused && !runDisabled;
          return (
            <button
              key={entry.action}
              type="button"
              role="menuitem"
              disabled={runDisabled}
              aria-disabled={runDisabled}
              title={runDisabled ? "Can't run from trainer battles" : undefined}
              onMouseEnter={() => {
                if (!runDisabled) {
                  setCursor(i);
                  setFocused(true);
                }
              }}
              onClick={() => { if (!runDisabled) onSelect(entry.action); }}
              className="relative font-mono font-bold uppercase tracking-widest text-left"
              style={{
                backgroundColor: active ? INK : CREAM,
                color: active ? CREAM : INK,
                border: `2px solid ${INK}`,
                padding: "0.625rem 0.75rem 0.625rem 1.75rem",
                fontSize: "0.95rem",
                lineHeight: 1,
                transition: "none",
                imageRendering: "pixelated",
                opacity: runDisabled ? 0.4 : 1,
                cursor: runDisabled ? "not-allowed" : "pointer",
              }}
            >
              {active && (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: "0.5rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: CREAM,
                    fontSize: "0.95rem",
                    lineHeight: 1,
                  }}
                >
                  &#9654;
                </span>
              )}
              {entry.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
